import { Controller } from "@hotwired/stimulus"
import { mapService } from "@/services/map_service"
import { errorHandler } from "@/services/error_handler_service"
import { MAP_CONSTANTS } from "@/constants"
import type { MapInstance, Bounds } from "@/types/map_provider"

interface ElevationPoint {
  lat: number
  lng: number
  elevation: number
}

const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 5

export default class extends Controller {
  static override values = {
    points: Array,
    propertyBoundary: String,
    pollUrl: String
  }

  declare readonly pointsValue: ElevationPoint[]
  declare readonly propertyBoundaryValue: string
  declare readonly hasPointsValue: boolean
  declare readonly hasPropertyBoundaryValue: boolean
  declare readonly pollUrlValue: string
  declare readonly hasPollUrlValue: boolean

  private map?: MapInstance
  private pollTimer: ReturnType<typeof setTimeout> | undefined
  private pollAttempts = 0
  private markers: object[] = []
  private contourPolygons: { setMap: (map: null) => void }[] = []

  override async connect() {
    try {
      await mapService.load()
      await this.initMap()
      const partial = (this.element as HTMLElement).dataset.elevationMapPartialValue === 'true'
      if (partial && this.hasPollUrlValue) {
        this.schedulePoll()
      }
    } catch (error) {
      console.error('Failed to load map service:', error)
      this.showErrorMessage('Failed to load map. Please refresh the page.')
    }
  }

  override disconnect() {
    this.stopPolling()
  }

  async initMap() {
    if (!this.hasPointsValue || this.pointsValue.length === 0) {
      if (this.hasPollUrlValue) {
        this.showLoadingMessage()
      } else {
        this.showNoDataMessage()
      }
      return
    }

    // Calculate bounds from elevation points
    const bounds = this.calculateBounds()

    const mapTypeId = mapService.getMapTypeId()

    // Get center from bounds or use first point as fallback
    let center: { lat: number; lng: number }
    if (bounds.getCenter) {
      center = bounds.getCenter()
    } else {
      // Use first elevation point as center (we know array has elements due to check above)
      const firstPoint = this.pointsValue[0]
      if (firstPoint) {
        center = { lat: firstPoint.lat, lng: firstPoint.lng }
      } else {
        // This should never happen due to the check above, but TypeScript needs it
        center = { lat: 0, lng: 0 }
      }
    }

    this.map = await mapService.createMap(this.element.querySelector('#elevation-map') as HTMLElement, {
      center: center,
      zoom: 16,
      mapTypeId: mapTypeId.TERRAIN,
      mapTypeControl: true
    })

    // Fit map to bounds
    if (this.map && typeof this.map.fitBounds === 'function') {
      this.map.fitBounds(bounds)
    }

    // Draw property boundary
    if (this.hasPropertyBoundaryValue && this.propertyBoundaryValue) {
      await this.drawPropertyBoundary()
    }

    // Draw elevation points and contour lines
    await this.drawElevationData()
  }

  calculateBounds(): Bounds {
    const bounds = mapService.createBounds()

    for (const point of this.pointsValue) {
      const latLng = mapService.createLatLng(point.lat, point.lng)
      bounds.extend(latLng)
    }

    return bounds
  }

  async drawPropertyBoundary() {
    try {
      // Parse GeoJSON property boundary
      const geoJSON = JSON.parse(this.propertyBoundaryValue)
      if (geoJSON.geometry && geoJSON.geometry.type === 'Polygon' && geoJSON.geometry.coordinates) {
        // Convert GeoJSON coordinates to map coordinates
        const path = geoJSON.geometry.coordinates[0].map((coord: number[]) => ({
          lat: coord[1],
          lng: coord[0]
        }))

        if (path.length > 0) {
          await mapService.createPolygon({
            paths: path,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#FF0000',
            fillOpacity: 0.1,
            map: this.map || null
          })
        }
      }
    } catch (e) {
      console.error('Error drawing property boundary:', e)
    }
  }

  async drawElevationData() {
    if (!this.map) return

    // Create elevation markers
    for (const point of this.pointsValue) {
      const markerElement = this.createElevationMarkerElement(point.elevation)

      const marker = await mapService.createMarker({
        position: { lat: point.lat, lng: point.lng },
        map: this.map,
        title: `${point.elevation}m elevation`,
        content: markerElement
      })
      this.markers.push(marker)
    }

    // Draw contour lines
    await this.drawContourLines(this.pointsValue)
  }

  createElevationMarkerElement(elevation: number): HTMLElement {
    const colors = MAP_CONSTANTS.ELEVATION_COLORS
    const colorIndex = Math.min(Math.floor(elevation / 100), colors.length - 1)
    const color = colors[colorIndex] || MAP_CONSTANTS.DEFAULT_ELEVATION_COLOR

    const wrapper = document.createElement('div')
    wrapper.style.display = 'flex'
    wrapper.style.flexDirection = 'column'
    wrapper.style.alignItems = 'center'
    wrapper.style.gap = '2px'

    const dot = document.createElement('div')
    dot.style.width = '12px'
    dot.style.height = '12px'
    dot.style.borderRadius = '50%'
    dot.style.backgroundColor = color
    dot.style.border = '2px solid white'
    dot.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)'
    dot.style.flexShrink = '0'

    const label = document.createElement('div')
    label.textContent = `${Math.round(elevation)}m`
    label.style.fontSize = '10px'
    label.style.fontWeight = '600'
    label.style.color = '#333'
    label.style.backgroundColor = 'rgba(255,255,255,0.85)'
    label.style.padding = '1px 3px'
    label.style.borderRadius = '3px'
    label.style.whiteSpace = 'nowrap'
    label.style.boxShadow = '0 1px 2px rgba(0,0,0,0.2)'
    label.style.lineHeight = '1.2'

    wrapper.appendChild(dot)
    wrapper.appendChild(label)
    wrapper.title = `${elevation}m elevation`

    return wrapper
  }

  async drawContourLines(points: ElevationPoint[]) {
    if (!this.map || points.length < 3) return

    try {
      // Group points by elevation ranges
      const elevationRanges = this.groupPointsByElevation(points)

      // Ensure elevationRanges is an object before using Object.entries
      if (typeof elevationRanges === 'object' && elevationRanges !== null) {
        for (const [elevation, range] of Object.entries(elevationRanges)) {
          if (range && Array.isArray(range) && range.length >= 3) {
            // Create a polygon for this elevation range
            const path = range.map(point => ({
              lat: point.lat,
              lng: point.lng
            }))

            const polygon = await mapService.createPolygon({
              paths: path,
              strokeColor: this.getContourColor(parseInt(elevation)),
              strokeOpacity: 0.6,
              strokeWeight: 1,
              fillColor: this.getContourColor(parseInt(elevation)),
              fillOpacity: 0.1,
              map: this.map
            })
            this.contourPolygons.push(polygon as { setMap: (map: null) => void })
          }
        }
      }
    } catch (error) {
      console.error('Error drawing contour lines:', error)
    }
  }

  groupPointsByElevation(points: ElevationPoint[]): Record<number, ElevationPoint[]> {
    const ranges: Record<number, ElevationPoint[]> = {}
    const interval = 10 // 10 meter intervals

    try {
      points.forEach(point => {
        if (point && typeof point.elevation === 'number') {
          const elevationRange = Math.floor(point.elevation / interval) * interval
          if (!ranges[elevationRange]) {
            ranges[elevationRange] = []
          }
          ranges[elevationRange].push(point)
        }
      })
    } catch (error) {
      console.error('Error grouping points by elevation:', error)
    }

    return ranges
  }

  getContourColor(elevation: number): string {
    // Color gradient from blue (low) to green (high)
    const colors = MAP_CONSTANTS.CONTOUR_LINE_COLORS

    const index = Math.min(Math.floor(elevation / 50), colors.length - 1)
    return colors[index] || MAP_CONSTANTS.DEFAULT_ELEVATION_COLOR
  }

  schedulePoll() {
    if (this.pollAttempts >= MAX_POLL_ATTEMPTS) return
    this.pollTimer = setTimeout(() => this.poll(), POLL_INTERVAL_MS)
  }

  stopPolling() {
    if (this.pollTimer !== undefined) {
      clearTimeout(this.pollTimer)
      this.pollTimer = undefined
    }
  }

  async poll() {
    this.pollAttempts++
    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { Accept: 'application/json' }
      })
      if (!response.ok) {
        this.schedulePoll()
        return
      }

      const data = await response.json() as {
        points: ElevationPoint[]
        partial: boolean
        total_sampled: number
        min_elevation: number
        max_elevation: number
        avg_elevation: number
      }

      if (data.points && data.points.length > 0) {
        await this.updateMapWithNewData(data)
        this.updateMetrics(data)

        if (data.partial) {
          this.schedulePoll()
        } else {
          this.stopPolling()
          this.hideLoadingIndicator()
        }
      } else {
        this.schedulePoll()
      }
    } catch {
      this.schedulePoll()
    }
  }

  async updateMapWithNewData(data: { points: ElevationPoint[] }) {
    if (!this.map) {
      // Map not initialized yet — initialize it now
      const mapEl = this.element.querySelector('#elevation-map') as HTMLElement
      if (mapEl) {
        mapEl.innerHTML = ''
        mapEl.style.display = ''
      }
      // Re-set points value and re-init the map
      // We update the DOM attribute so pointsValue reflects the new data
      ;(this.element as HTMLElement).dataset.elevationMapPointsValue = JSON.stringify(data.points)
      await this.initMap()
      return
    }

    // Clear existing markers by removing and re-adding all
    await this.redrawMarkers(data.points)
  }

  async redrawMarkers(points: ElevationPoint[]) {
    if (!this.map) return

    // Remove existing markers
    for (const marker of this.markers) {
      if (marker && typeof (marker as { remove?: () => void }).remove === 'function') {
        (marker as { remove: () => void }).remove()
      }
    }
    this.markers = []

    // Remove existing contour polygons
    for (const polygon of this.contourPolygons) {
      polygon.setMap(null)
    }
    this.contourPolygons = []

    // Add updated markers
    for (const point of points) {
      const markerElement = this.createElevationMarkerElement(point.elevation)
      const marker = await mapService.createMarker({
        position: { lat: point.lat, lng: point.lng },
        map: this.map,
        title: `${point.elevation}m elevation`,
        content: markerElement
      })
      this.markers.push(marker)
    }

    // Redraw contour lines with full point set
    await this.drawContourLines(points)
  }

  updateMetrics(data: { points: ElevationPoint[]; min_elevation: number; max_elevation: number; avg_elevation: number }) {
    const metricsContainer = document.querySelector('.elevation-metrics')
    if (!metricsContainer) return

    const cards = metricsContainer.querySelectorAll('.metric-card .metric-value')
    if (cards.length >= 3) {
      cards[0]!.textContent = String(Math.round(data.avg_elevation))
      cards[1]!.textContent = String(data.max_elevation - data.min_elevation)
      cards[2]!.textContent = String(data.min_elevation)
    }
  }

  showLoadingMessage() {
    const mapElement = this.element.querySelector('#elevation-map') as HTMLElement
    if (mapElement) {
      mapElement.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666;">
          <div style="text-align: center;" data-elevation-loading>
            <div class="spinner-border spinner-border-sm text-success mb-2" role="status"></div>
            <p>Fetching elevation data...</p>
          </div>
        </div>
      `
    }
  }

  hideLoadingIndicator() {
    const indicator = document.querySelector('[data-elevation-loading]')
    if (indicator) indicator.remove()
  }

  showNoDataMessage() {
    const mapElement = this.element.querySelector('#elevation-map') as HTMLElement
    if (mapElement) {
      mapElement.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666;">
          <div style="text-align: center;">
            <i class="bi bi-info-circle" style="font-size: 2rem; margin-bottom: 1rem;"></i>
            <p>No elevation data available for this location.</p>
          </div>
        </div>
      `
    }
  }

  showErrorMessage(message: string) {
    errorHandler.showError(message)
  }
}
